import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  Database,
  ExternalLink,
  FileText,
  ImageIcon,
  Layers,
  LinkIcon,
  ListChecks,
  Loader2,
  MessageSquare,
  Music,
  PlayCircle,
  Save,
  Search,
  Trash2,
  Upload,
  Video,
} from 'lucide-react';
import type { LegacyExperience } from '../types/legacy';
import {
  buildRetrievalText,
  createKnowledgeDocument,
  defaultAssetRoleForType,
  defaultVisibilityForRole,
  extractKnowledgeTextFromDocument,
  extractKnowledgeTextFromFile,
  inferKnowledgeFileType,
  indexKnowledgeDocumentFromText,
  listKnowledgeChunksForDocument,
  listKnowledgeUsageLogsForExperience,
  logKnowledgeUsage,
  listKnowledgeDocumentsForExperience,
  retrieveKnowledgeForExperience,
  softDisableKnowledgeDocument,
  updateKnowledgeDocument,
  uploadKnowledgeMediaFile,
  type KnowledgeAssetRole,
  type KnowledgeChunk,
  type KnowledgeDocument,
  type KnowledgeDocumentInput,
  type KnowledgeFileType,
  type KnowledgeRetrieveResult,
  type KnowledgeSourceType,
  type KnowledgeUsageLog,
  type KnowledgeVisibility,
} from '../services/knowledgeMediaService';

interface ExperienceKnowledgeMediaPanelProps {
  experience: LegacyExperience;
}

type EditableKnowledgeDocument = KnowledgeDocument & {
  tagsText: string;
  hintsText: string;
};

type KnowledgePanelSection = 'library' | 'gallery' | 'retrieval' | 'logs';

const FILE_TYPE_LABELS: Record<KnowledgeFileType, string> = {
  pdf: 'PDF',
  docx: 'DOCX',
  txt: 'TXT',
  markdown: 'Markdown',
  image: 'Immagine',
  audio: 'Audio',
  video: 'Video',
  url: 'Link',
  other: 'Altro',
};

const SOURCE_TYPE_LABELS: Record<KnowledgeSourceType, string> = {
  primary_source: 'Fonte primaria',
  secondary_source: 'Fonte secondaria',
  teaching_material: 'Materiale didattico',
  archive: 'Archivio',
  link: 'Link',
  visual_material: 'Materiale visivo',
  audio_material: 'Materiale audio',
  video_material: 'Materiale video',
  sponsor_material: 'Materiale sponsor',
};

const ROLE_LABELS: Record<KnowledgeAssetRole, string> = {
  knowledge_source: 'Fonte conoscenza',
  media_gallery: 'Galleria utente',
  both: 'Fonte + galleria',
};

const VISIBILITY_LABELS: Record<KnowledgeVisibility, string> = {
  internal_only: 'Solo interno',
  usable_in_answers: 'Usabile nelle risposte',
  showable_to_user: 'Mostrabile all utente',
  downloadable: 'Scaricabile',
};

const statusLabel: Record<KnowledgeDocument['status'], string> = {
  draft: 'Bozza',
  uploaded: 'Caricato',
  metadata_ready: 'Metadati pronti',
  indexed: 'Indicizzato',
  disabled: 'Disattivato',
  failed: 'Errore',
};

const toCsv = (items: string[]) => items.join(', ');

const parseList = (value: string): string[] =>
  String(value || '')
    .split(/[\n,;]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const getTitleFromFile = (file: File): string =>
  String(file.name || 'Nuovo materiale')
    .replace(/\.[^.]+$/, '')
    .replace(/[-_]+/g, ' ')
    .trim() || 'Nuovo materiale';

const defaultSourceTypeForFile = (fileType: KnowledgeFileType): KnowledgeSourceType => {
  if (fileType === 'image') return 'visual_material';
  if (fileType === 'audio') return 'audio_material';
  if (fileType === 'video') return 'video_material';
  if (fileType === 'url') return 'link';
  return 'teaching_material';
};

const iconForFileType = (fileType: KnowledgeFileType) => {
  if (fileType === 'image') return ImageIcon;
  if (fileType === 'audio') return Music;
  if (fileType === 'video') return Video;
  if (fileType === 'url') return LinkIcon;
  return FileText;
};

const toEditable = (doc: KnowledgeDocument): EditableKnowledgeDocument => ({
  ...doc,
  tagsText: toCsv(doc.tags),
  hintsText: doc.mediaIntentHints.join('\n'),
});

const fromEditable = (doc: EditableKnowledgeDocument): KnowledgeDocumentInput => {
  const tags = parseList(doc.tagsText);
  const mediaIntentHints = parseList(doc.hintsText);
  const base: KnowledgeDocumentInput = {
    organizationId: doc.organizationId ?? null,
    experienceId: doc.experienceId,
    experienceName: doc.experienceName || '',
    characterName: doc.characterName || '',
    title: doc.title,
    description: doc.description,
    fileType: doc.fileType,
    fileUrl: doc.fileUrl,
    storagePath: doc.storagePath,
    externalUrl: doc.externalUrl,
    thumbnailUrl: doc.thumbnailUrl,
    language: doc.language || 'it',
    sourceType: doc.sourceType,
    assetRole: doc.assetRole,
    author: doc.author,
    date: doc.date,
    rightsStatus: doc.rightsStatus || 'da_verificare',
    visibility: doc.visibility,
    retrievalEnabled: doc.retrievalEnabled,
    showToUserEnabled: doc.showToUserEnabled,
    priority: Number(doc.priority || 5),
    tags,
    summary: doc.summary,
    mediaIntentHints,
    retrievalText: '',
    status: doc.status,
    embeddingStatus: doc.embeddingStatus,
    transcriptionStatus: doc.transcriptionStatus,
    durationSeconds: doc.durationSeconds ?? null,
    active: doc.active,
  };
  return {
    ...base,
    retrievalText: buildRetrievalText(base),
  };
};

export default function ExperienceKnowledgeMediaPanel({ experience }: ExperienceKnowledgeMediaPanelProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [documents, setDocuments] = useState<KnowledgeDocument[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [draft, setDraft] = useState<EditableKnowledgeDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [activeSection, setActiveSection] = useState<KnowledgePanelSection>('library');
  const [chunks, setChunks] = useState<KnowledgeChunk[]>([]);
  const [logs, setLogs] = useState<KnowledgeUsageLog[]>([]);
  const [retrievalQuery, setRetrievalQuery] = useState('');
  const [retrievalResults, setRetrievalResults] = useState<KnowledgeRetrieveResult[]>([]);
  const [retrieving, setRetrieving] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkTitle, setLinkTitle] = useState('');
  const [linkDescription, setLinkDescription] = useState('');

  const experienceId = Number(experience.id || 0);

  const loadDocuments = useCallback(async () => {
    if (!experienceId) return;
    setLoading(true);
    setError('');
    try {
      const items = await listKnowledgeDocumentsForExperience(experienceId);
      setDocuments(items);
      setSelectedId((current) => current || items[0]?.id || '');
      const usageLogs = await listKnowledgeUsageLogsForExperience(experienceId);
      setLogs(usageLogs);
    } catch (err) {
      console.error('[KnowledgeMedia] load failed', err);
      setError('Impossibile caricare la galleria Knowledge & Media.');
    } finally {
      setLoading(false);
    }
  }, [experienceId]);

  useEffect(() => {
    void loadDocuments();
  }, [loadDocuments]);

  useEffect(() => {
    const selected = documents.find((item) => item.id === selectedId) || null;
    setDraft(selected ? toEditable(selected) : null);
  }, [documents, selectedId]);

  useEffect(() => {
    let cancelled = false;
    const loadChunks = async () => {
      if (!selectedId) {
        setChunks([]);
        return;
      }
      try {
        const items = await listKnowledgeChunksForDocument(selectedId);
        if (!cancelled) setChunks(items);
      } catch (err) {
        if (!cancelled) setChunks([]);
      }
    };
    void loadChunks();
    return () => {
      cancelled = true;
    };
  }, [selectedId]);

  const selectedDocument = useMemo(
    () => documents.find((item) => item.id === selectedId) || null,
    [documents, selectedId],
  );

  const mediaDocuments = useMemo(
    () => documents.filter((item) => item.showToUserEnabled || item.assetRole === 'media_gallery' || item.assetRole === 'both'),
    [documents],
  );

  const sourceDocuments = useMemo(
    () => documents.filter((item) => item.retrievalEnabled || item.assetRole === 'knowledge_source' || item.assetRole === 'both'),
    [documents],
  );

  const indexedCount = documents.filter((item) => item.embeddingStatus === 'indexed' || item.status === 'indexed').length;

  const baseDocumentInput = useCallback((params: {
    title: string;
    description?: string;
    fileType: KnowledgeFileType;
    fileUrl: string;
    storagePath?: string;
    externalUrl?: string;
  }): KnowledgeDocumentInput => {
    const assetRole = defaultAssetRoleForType(params.fileType);
    const visibility = defaultVisibilityForRole(assetRole);
    const showToUserEnabled = assetRole !== 'knowledge_source';
    const input: KnowledgeDocumentInput = {
      organizationId: Number(experience.idOrganization || 0) || null,
      experienceId,
      experienceName: String(experience.name || ''),
      characterName: String(experience.nomeChatbot || ''),
      title: params.title,
      description: params.description || '',
      fileType: params.fileType,
      fileUrl: params.fileUrl,
      storagePath: params.storagePath,
      externalUrl: params.externalUrl,
      thumbnailUrl: params.fileType === 'image' ? params.fileUrl : '',
      language: String(experience.language || 'it'),
      sourceType: defaultSourceTypeForFile(params.fileType),
      assetRole,
      author: '',
      date: '',
      rightsStatus: 'da_verificare',
      visibility,
      retrievalEnabled: true,
      showToUserEnabled,
      priority: 5,
      tags: [],
      summary: '',
      mediaIntentHints: [],
      retrievalText: '',
      status: 'uploaded',
      embeddingStatus: 'not_indexed',
      transcriptionStatus: params.fileType === 'audio' || params.fileType === 'video' ? 'not_transcribed' : 'not_required',
      durationSeconds: null,
      active: true,
    };
    return {
      ...input,
      retrievalText: buildRetrievalText(input),
    };
  }, [experience, experienceId]);

  const selectCreated = async (created: KnowledgeDocument) => {
    setDocuments((prev) => [created, ...prev]);
    setSelectedId(created.id);
    setInfo('Materiale aggiunto.');
  };

  const onFileChange = async (file: File | null) => {
    if (!file || !experienceId) return;
    setUploading(true);
    setError('');
    setInfo('');
    try {
      const fileType = inferKnowledgeFileType(file);
      const extractedText = await extractKnowledgeTextFromFile(file);
      const uploaded = await uploadKnowledgeMediaFile({ file, experienceId });
      const documentInput = baseDocumentInput({
        title: getTitleFromFile(file),
        fileType,
        fileUrl: uploaded.fileUrl,
        storagePath: uploaded.storagePath,
        description: extractedText ? 'Testo letto dal file e pronto per la ricerca.' : '',
      });
      if (extractedText) {
        documentInput.summary = extractedText.slice(0, 1800);
        documentInput.retrievalText = buildRetrievalText(documentInput);
        documentInput.status = 'metadata_ready';
        documentInput.embeddingStatus = 'metadata_ready';
      }
      const created = await createKnowledgeDocument(documentInput);
      let createdForState = created;
      if (extractedText) {
        const createdChunks = await indexKnowledgeDocumentFromText(created, extractedText);
        createdForState = {
          ...created,
          summary: extractedText.slice(0, 1800),
          status: createdChunks.length > 0 ? 'indexed' : 'metadata_ready',
          embeddingStatus: createdChunks.length > 0 ? 'indexed' : 'metadata_ready',
        };
        setChunks(createdChunks);
      }
      await selectCreated(createdForState);
    } catch (err) {
      console.error('[KnowledgeMedia] upload failed', err);
      setError('Upload non riuscito. Verifica permessi Firebase Storage/Firestore e riprova.');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onAddLink = async () => {
    const url = linkUrl.trim();
    if (!url) {
      setError('Inserisci un URL valido prima di aggiungere il link.');
      return;
    }
    if (/^(data|blob|javascript):/i.test(url)) {
      setError('Questo tipo di sorgente non è supportato. Carica l’immagine come file oppure usa un URL pubblico https.');
      return;
    }
    setSaving(true);
    setError('');
    setInfo('');
    try {
      const created = await createKnowledgeDocument(baseDocumentInput({
        title: linkTitle.trim() || url,
        description: linkDescription.trim(),
        fileType: 'url',
        fileUrl: url,
        externalUrl: url,
      }));
      setLinkUrl('');
      setLinkTitle('');
      setLinkDescription('');
      await selectCreated(created);
    } catch (err) {
      console.error('[KnowledgeMedia] link create failed', err);
      setError('Link non salvato. Riprova.');
    } finally {
      setSaving(false);
    }
  };

  const onSaveDraft = async (prepareMetadata = false) => {
    if (!draft) return;
    setSaving(true);
    setError('');
    setInfo('');
    try {
      const payload = fromEditable({
        ...draft,
        status: prepareMetadata ? 'metadata_ready' : draft.status,
        embeddingStatus: prepareMetadata ? 'metadata_ready' : draft.embeddingStatus,
      });
      await updateKnowledgeDocument(draft.id, payload);
      const updated: KnowledgeDocument = {
        ...draft,
        ...payload,
        updatedAt: new Date(),
      };
      setDocuments((prev) => prev.map((item) => (item.id === draft.id ? updated : item)));
      setInfo(prepareMetadata ? 'Metadati pronti.' : 'Metadati salvati.');
    } catch (err) {
      console.error('[KnowledgeMedia] save failed', err);
      setError('Salvataggio non riuscito. Riprova.');
    } finally {
      setSaving(false);
    }
  };

  const onDisable = async () => {
    if (!draft) return;
    const confirmed = window.confirm(`Disattivare "${draft.title}" dalla galleria?`);
    if (!confirmed) return;
    setSaving(true);
    setError('');
    setInfo('');
    try {
      await softDisableKnowledgeDocument(draft.id);
      setDocuments((prev) => prev.filter((item) => item.id !== draft.id));
      setSelectedId((prev) => {
        if (prev !== draft.id) return prev;
        const next = documents.find((item) => item.id !== draft.id);
        return next?.id || '';
      });
      setInfo('Materiale disattivato.');
    } catch (err) {
      console.error('[KnowledgeMedia] disable failed', err);
      setError('Disattivazione non riuscita.');
    } finally {
      setSaving(false);
    }
  };

  const onIndexSelected = async () => {
    if (!draft) return;
    setSaving(true);
    setError('');
    setInfo('');
    try {
      const payload = fromEditable({
        ...draft,
        status: 'metadata_ready',
        embeddingStatus: 'metadata_ready',
      });
      await updateKnowledgeDocument(draft.id, payload);
      const documentForIndex: KnowledgeDocument = {
        ...draft,
        ...payload,
      };
      const extractedText = await extractKnowledgeTextFromDocument(documentForIndex).catch((err) => {
        console.warn('[KnowledgeMedia] source text extraction failed, using metadata fallback', err);
        return '';
      });
      const sourceText = extractedText || payload.summary || payload.retrievalText;
      const createdChunks = await indexKnowledgeDocumentFromText(documentForIndex, sourceText);
      const updated: KnowledgeDocument = {
        ...documentForIndex,
        summary: extractedText ? extractedText.slice(0, 1800) : documentForIndex.summary,
        status: createdChunks.length > 0 ? 'indexed' : 'metadata_ready',
        embeddingStatus: createdChunks.length > 0 ? 'indexed' : 'metadata_ready',
        updatedAt: new Date(),
      };
      if (extractedText) {
        await updateKnowledgeDocument(draft.id, {
          ...payload,
          summary: updated.summary,
          status: updated.status,
          embeddingStatus: updated.embeddingStatus,
        });
      }
      setDocuments((prev) => prev.map((item) => (item.id === draft.id ? updated : item)));
      setChunks(createdChunks);
      setInfo(
        createdChunks.length > 0
          ? `Preparazione completata: ${createdChunks.length} estratti pronti per la ricerca.`
          : 'Metadati salvati. Aggiungi un abstract o un testo descrittivo per rendere il materiale ricercabile.',
      );
    } catch (err) {
      console.error('[KnowledgeMedia] local index failed', err);
      setError('Preparazione del materiale non riuscita.');
    } finally {
      setSaving(false);
    }
  };

  const onRunRetrieval = async () => {
    setRetrieving(true);
    setError('');
    try {
      const results = await retrieveKnowledgeForExperience(experienceId, retrievalQuery, {
        includeMedia: true,
        maxDocuments: 6,
      });
      setRetrievalResults(results);
      const usageLogs = await listKnowledgeUsageLogsForExperience(experienceId);
      setLogs(usageLogs);
    } catch (err) {
      console.error('[KnowledgeMedia] retrieval preview failed', err);
      setError('Test retrieval non riuscito.');
    } finally {
      setRetrieving(false);
    }
  };

  const onLogShown = async () => {
    if (!draft) return;
    try {
      const log = await logKnowledgeUsage({
        experienceId,
        documentId: draft.id,
        usageType: 'shown',
        score: null,
      });
      setLogs((prev) => [log, ...prev].slice(0, 50));
      setInfo('Evento mostrato registrato.');
    } catch (err) {
      // Log non bloccante: la simulazione resta utilizzabile anche senza persistenza.
    }
  };

  const updateDraft = <K extends keyof EditableKnowledgeDocument>(key: K, value: EditableKnowledgeDocument[K]) => {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  };

  const previewUrl = draft?.fileUrl || draft?.externalUrl || '';
  const PreviewIcon = draft ? iconForFileType(draft.fileType) : FileText;
  const visibleDocuments = activeSection === 'gallery' ? mediaDocuments : documents;
  const sections: Array<{ id: KnowledgePanelSection; label: string; count: number; icon: typeof FileText }> = [
    { id: 'library', label: 'Knowledge Base', count: sourceDocuments.length, icon: Database },
    { id: 'gallery', label: 'Media Gallery', count: mediaDocuments.length, icon: ImageIcon },
    { id: 'retrieval', label: 'Test retrieval', count: indexedCount, icon: Search },
    { id: 'logs', label: 'Log utilizzo', count: logs.length, icon: ListChecks },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-primary/25 bg-primary/5 p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h3 className="font-semibold text-text-primary">Knowledge &amp; Media</h3>
              <span className="inline-flex items-center rounded-full border border-amber-300 bg-amber-100 px-2.5 py-1 text-[11px] font-extrabold uppercase tracking-[0.16em] text-amber-800 shadow-sm">
                BETA
              </span>
            </div>
            <p className="mt-1 text-xs text-text-secondary">
              Fonti e contenuti mostrabili collegati a questa esperienza.
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2">
            {sections.map((section) => {
              const SectionIcon = section.icon;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActiveSection(section.id)}
                  className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition ${
                    activeSection === section.id
                      ? 'border-primary bg-primary text-white'
                      : 'border-border bg-white text-text-secondary hover:text-text-primary hover:border-primary/40'
                  }`}
                >
                  <SectionIcon size={15} />
                  {section.label}
                  <span className={`rounded-full px-1.5 py-0.5 text-[11px] ${
                    activeSection === section.id ? 'bg-white/20 text-white' : 'bg-gray-100 text-text-secondary'
                  }`}>
                    {section.count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept=".pdf,.doc,.docx,.txt,.md,image/*,audio/*,video/*"
              onChange={(e) => void onFileChange(e.target.files?.[0] || null)}
            />
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-2"
              disabled={uploading || saving || !experienceId}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />}
              {uploading ? 'Upload...' : 'Carica file'}
            </button>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-white p-3">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-2">
          <input
            className="rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://... aggiungi link, video o archivio"
          />
          <input
            className="rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            value={linkTitle}
            onChange={(e) => setLinkTitle(e.target.value)}
            placeholder="Titolo link"
          />
          <button
            type="button"
            className="btn-outline inline-flex items-center justify-center gap-2"
            disabled={saving || !linkUrl.trim()}
            onClick={() => void onAddLink()}
          >
            <LinkIcon size={15} />
            Aggiungi link
          </button>
        </div>
        <textarea
          className="mt-2 w-full rounded-lg border border-border px-3 py-2 text-sm min-h-[70px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
          value={linkDescription}
          onChange={(e) => setLinkDescription(e.target.value)}
          placeholder="Descrizione opzionale del link e quando usarlo nella conversazione"
        />
      </div>

      {error && (
        <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-sm text-error flex items-start gap-2">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {info && (
        <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-800 flex items-start gap-2">
          <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
          <span>{info}</span>
        </div>
      )}

      {activeSection === 'retrieval' && (
        <section className="rounded-xl border border-border bg-white p-4 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block flex-1 min-w-[16rem]">
              <span className="text-sm font-medium text-text-secondary block mb-1.5">Domanda o intento utente</span>
              <input
                className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                value={retrievalQuery}
                onChange={(e) => setRetrievalQuery(e.target.value)}
                placeholder="Es. Vorrei vedere una mappa del foro romano"
              />
            </label>
            <button
              type="button"
              className="btn-primary inline-flex items-center gap-2"
              onClick={() => void onRunRetrieval()}
              disabled={retrieving}
            >
              {retrieving ? <Loader2 size={15} className="animate-spin" /> : <PlayCircle size={15} />}
              Esegui test
            </button>
          </div>

          <div className="space-y-3">
            {retrievalResults.length === 0 ? (
              <div className="rounded-lg border border-border bg-gray-50 p-4 text-sm text-text-secondary">
                Inserisci una domanda per vedere quali fonti e media verrebbero selezionati.
              </div>
            ) : retrievalResults.map((result) => {
              const ResultIcon = iconForFileType(result.document.fileType);
              return (
                <article key={result.document.id} className="rounded-xl border border-border bg-white p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <ResultIcon size={18} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="font-semibold text-text-primary">{result.document.title}</h4>
                        <span className="text-xs rounded-full bg-primary/10 text-primary px-2 py-0.5">
                          rilevanza {result.score.toFixed(1)}
                        </span>
                        {result.document.showToUserEnabled && (
                          <span className="text-xs rounded-full bg-green-50 text-green-700 px-2 py-0.5">media</span>
                        )}
                      </div>
                      <p className="text-xs text-text-secondary mt-1">{result.reason}</p>
                      {result.chunks.length > 0 && (
                        <div className="mt-3 space-y-2">
                          {result.chunks.map((chunk) => (
                            <div key={chunk.id} className="rounded-lg bg-gray-50 border border-border p-3 text-xs text-text-secondary whitespace-pre-wrap">
                              {chunk.chunkText.slice(0, 700)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
      )}

      {activeSection === 'logs' && (
        <section className="rounded-xl border border-border bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2">
            <ListChecks size={16} className="text-primary" />
            <h3 className="font-semibold text-text-primary">Log utilizzo materiali</h3>
          </div>
          {logs.length === 0 ? (
            <div className="p-6 text-sm text-text-secondary">Nessun evento registrato.</div>
          ) : (
            <div className="divide-y divide-border">
              {logs.map((log) => {
                const source = documents.find((item) => item.id === log.documentId);
                return (
                  <div key={log.id} className="p-4 flex flex-wrap items-center justify-between gap-3 text-sm">
                    <div>
                      <div className="font-medium text-text-primary">{source?.title || log.documentId}</div>
                      <div className="text-xs text-text-secondary">
                        {log.usageType} {log.score ? `· rilevanza ${log.score.toFixed(1)}` : ''}
                      </div>
                    </div>
                    <div className="text-xs text-text-secondary">
                      {log.createdAt ? log.createdAt.toLocaleString() : '-'}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {(activeSection === 'library' || activeSection === 'gallery') && (
      <div className="grid grid-cols-1 xl:grid-cols-[0.9fr_1.3fr] gap-4">
        <section className="rounded-xl border border-border bg-white overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between gap-3">
            <div>
              <h3 className="font-semibold text-text-primary">
                {activeSection === 'gallery' ? 'Media mostrabili' : 'Fonti e documenti'}
              </h3>
              <p className="text-xs text-text-secondary">{visibleDocuments.length} materiali associati</p>
            </div>
            {loading && <Loader2 size={16} className="animate-spin text-primary" />}
          </div>

          {visibleDocuments.length === 0 && !loading ? (
            <div className="p-6 text-sm text-text-secondary">
              Nessun materiale presente in questa sezione.
            </div>
          ) : (
            <div className="max-h-[58vh] overflow-y-auto p-2 space-y-2">
              {visibleDocuments.map((item) => {
                const ItemIcon = iconForFileType(item.fileType);
                const selected = item.id === selectedId;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setSelectedId(item.id)}
                    className={`w-full text-left rounded-lg border p-3 transition ${
                      selected
                        ? 'border-primary bg-primary/5 shadow-sm'
                        : 'border-border bg-white hover:border-primary/40'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <ItemIcon size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-semibold text-text-primary truncate">{item.title || 'Senza titolo'}</div>
                        <div className="text-xs text-text-secondary truncate">
                          {FILE_TYPE_LABELS[item.fileType]} · {ROLE_LABELS[item.assetRole]} · {statusLabel[item.status]}
                        </div>
                        <div className="flex flex-wrap gap-1 mt-2">
                          {item.retrievalEnabled && (
                            <span className="text-[11px] rounded-full bg-blue-50 text-blue-700 px-2 py-0.5">retrieval</span>
                          )}
                          {item.showToUserEnabled && (
                            <span className="text-[11px] rounded-full bg-green-50 text-green-700 px-2 py-0.5">showable</span>
                          )}
                          {item.tags.slice(0, 2).map((tag) => (
                            <span key={tag} className="text-[11px] rounded-full bg-gray-100 text-text-secondary px-2 py-0.5">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </section>

        <section className="rounded-xl border border-border bg-white p-4">
          {!draft || !selectedDocument ? (
            <div className="min-h-[28rem] flex items-center justify-center text-sm text-text-secondary">
              Seleziona un materiale per modificarne metadati, visibilita e uso conversazionale.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-text-primary">Dettagli materiale</h3>
                  <p className="text-xs text-text-secondary">
                    Metadati, visibilita, testi ricercabili e comportamento conversazionale.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn-outline inline-flex items-center gap-2"
                    disabled={saving}
                    onClick={() => void onSaveDraft(false)}
                  >
                    <Save size={15} />
                    Salva metadati
                  </button>
                  <button
                    type="button"
                    className="btn-primary inline-flex items-center gap-2"
                    disabled={saving}
                    onClick={() => void onIndexSelected()}
                  >
                    {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
                    Prepara per la ricerca
                  </button>
                  {draft.showToUserEnabled && (
                    <button
                      type="button"
                      className="btn-outline inline-flex items-center gap-2"
                      disabled={saving}
                      onClick={() => void onLogShown()}
                    >
                      <MessageSquare size={15} />
                      Simula mostrato
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn-outline inline-flex items-center gap-2 text-error border-error/30 hover:bg-error/5"
                    disabled={saving}
                    onClick={() => void onDisable()}
                  >
                    <Trash2 size={15} />
                    Disattiva
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-gray-50/60 p-3">
                {draft.fileType === 'image' && previewUrl ? (
                  <img src={previewUrl} alt="" className="w-full max-h-72 object-contain rounded-lg bg-white border border-border" />
                ) : draft.fileType === 'audio' && previewUrl ? (
                  <audio src={previewUrl} controls className="w-full" />
                ) : draft.fileType === 'video' && previewUrl ? (
                  <video src={previewUrl} controls className="w-full max-h-72 rounded-lg bg-black" />
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                        <PreviewIcon size={22} />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-text-primary truncate">{draft.title}</div>
                        <div className="text-xs text-text-secondary truncate">{previewUrl}</div>
                      </div>
                    </div>
                    {previewUrl && (
                      <a
                        href={previewUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="btn-outline inline-flex items-center gap-2 shrink-0"
                      >
                        <ExternalLink size={15} />
                        Apri
                      </a>
                    )}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-sm font-medium text-text-secondary block mb-1.5">Titolo</span>
                  <input
                    className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={draft.title}
                    onChange={(e) => updateDraft('title', e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-text-secondary block mb-1.5">Lingua</span>
                  <input
                    className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={draft.language}
                    onChange={(e) => updateDraft('language', e.target.value)}
                    placeholder="it"
                  />
                </label>
              </div>

              <label className="block">
                <span className="text-sm font-medium text-text-secondary block mb-1.5">Descrizione</span>
                <textarea
                  className="w-full rounded-lg border border-border px-3 py-2.5 text-sm min-h-[90px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
                  value={draft.description}
                  onChange={(e) => updateDraft('description', e.target.value)}
                  placeholder="Cosa contiene e per quale tema e utile"
                />
              </label>

              <label className="block">
                <span className="text-sm font-medium text-text-secondary block mb-1.5">Abstract / contenuto recuperabile</span>
                <textarea
                  className="w-full rounded-lg border border-border px-3 py-2.5 text-sm min-h-[120px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
                  value={draft.summary}
                  onChange={(e) => updateDraft('summary', e.target.value)}
                  placeholder="Sintesi verificata o testo che l'agente puo usare per trovare questo materiale quando serve."
                />
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-sm font-medium text-text-secondary block mb-1.5">Tag</span>
                  <textarea
                    className="w-full rounded-lg border border-border px-3 py-2.5 text-sm min-h-[76px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={draft.tagsText}
                    onChange={(e) => updateDraft('tagsText', e.target.value)}
                    placeholder="storia, mappa, teatro romano"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-text-secondary block mb-1.5">Quando mostrarlo</span>
                  <textarea
                    className="w-full rounded-lg border border-border px-3 py-2.5 text-sm min-h-[76px] resize-y focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={draft.hintsText}
                    onChange={(e) => updateDraft('hintsText', e.target.value)}
                    placeholder="Domande su orientamento, mappe, teatro, percorso..."
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-sm font-medium text-text-secondary block mb-1.5">Tipo file</span>
                  <select
                    className="w-full rounded-lg border border-border px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={draft.fileType}
                    onChange={(e) => {
                      const fileType = e.target.value as KnowledgeFileType;
                      updateDraft('fileType', fileType);
                      updateDraft('sourceType', defaultSourceTypeForFile(fileType));
                    }}
                  >
                    {(Object.keys(FILE_TYPE_LABELS) as KnowledgeFileType[]).map((value) => (
                      <option key={value} value={value}>{FILE_TYPE_LABELS[value]}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-text-secondary block mb-1.5">Uso</span>
                  <select
                    className="w-full rounded-lg border border-border px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={draft.assetRole}
                    onChange={(e) => updateDraft('assetRole', e.target.value as KnowledgeAssetRole)}
                  >
                    {(Object.keys(ROLE_LABELS) as KnowledgeAssetRole[]).map((value) => (
                      <option key={value} value={value}>{ROLE_LABELS[value]}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-text-secondary block mb-1.5">Visibilita</span>
                  <select
                    className="w-full rounded-lg border border-border px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={draft.visibility}
                    onChange={(e) => updateDraft('visibility', e.target.value as KnowledgeVisibility)}
                  >
                    {(Object.keys(VISIBILITY_LABELS) as KnowledgeVisibility[]).map((value) => (
                      <option key={value} value={value}>{VISIBILITY_LABELS[value]}</option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label className="block">
                  <span className="text-sm font-medium text-text-secondary block mb-1.5">Tipo fonte</span>
                  <select
                    className="w-full rounded-lg border border-border px-3 py-2.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={draft.sourceType}
                    onChange={(e) => updateDraft('sourceType', e.target.value as KnowledgeSourceType)}
                  >
                    {(Object.keys(SOURCE_TYPE_LABELS) as KnowledgeSourceType[]).map((value) => (
                      <option key={value} value={value}>{SOURCE_TYPE_LABELS[value]}</option>
                    ))}
                  </select>
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-text-secondary block mb-1.5">Diritti</span>
                  <input
                    className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={draft.rightsStatus}
                    onChange={(e) => updateDraft('rightsStatus', e.target.value)}
                    placeholder="da_verificare"
                  />
                </label>
                <label className="block">
                  <span className="text-sm font-medium text-text-secondary block mb-1.5">Priorita</span>
                  <input
                    type="number"
                    min={1}
                    max={10}
                    className="w-full rounded-lg border border-border px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                    value={draft.priority}
                    onChange={(e) => updateDraft('priority', Number(e.target.value || 5))}
                  />
                </label>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 rounded-lg border border-border px-3 py-2.5 text-sm">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-primary"
                    checked={draft.retrievalEnabled}
                    onChange={(e) => updateDraft('retrievalEnabled', e.target.checked)}
                  />
                  Abilita retrieval
                </label>
                <label className="flex items-center gap-2 rounded-lg border border-border px-3 py-2.5 text-sm">
                  <input
                    type="checkbox"
                    className="w-4 h-4 accent-primary"
                    checked={draft.showToUserEnabled}
                    onChange={(e) => updateDraft('showToUserEnabled', e.target.checked)}
                  />
                  Mostrabile all&apos;utente
                </label>
              </div>

              <div className="rounded-lg border border-border bg-slate-950 p-3">
                <div className="text-xs text-green-300 font-mono whitespace-pre-wrap max-h-40 overflow-y-auto">
                  {buildRetrievalText(fromEditable(draft))}
                </div>
              </div>

              <div className="rounded-xl border border-border bg-white overflow-hidden">
                <div className="px-3 py-2 border-b border-border flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 font-semibold text-text-primary text-sm">
                    <Layers size={15} className="text-primary" />
                    Estratti pronti per la ricerca
                  </div>
                  <span className="text-xs text-text-secondary">{chunks.length}</span>
                </div>
                {chunks.length === 0 ? (
                  <div className="p-3 text-xs text-text-secondary">
                    Nessun testo pronto per la ricerca. Compila l&apos;abstract o importa un documento testuale, poi usa &quot;Prepara per la ricerca&quot;.
                  </div>
                ) : (
                  <div className="max-h-56 overflow-y-auto divide-y divide-border">
                    {chunks.map((chunk) => (
                      <div key={chunk.id} className="p-3 text-xs text-text-secondary">
                        <div className="font-semibold text-text-primary mb-1">Estratto {chunk.chunkOrder + 1}</div>
                        <div className="whitespace-pre-wrap">{chunk.chunkText.slice(0, 900)}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </section>
      </div>
      )}
    </div>
  );
}
